/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from 'reactstrap';
import { generateUrl } from '@routes/util';
import { BaseStepCompProps } from '../../../../../features/onboarding/types';
import { getAnalytics } from '@services/analytics/AnalyticsService';
import { AnalyticsEvents } from '@services/analytics/AnalyticsEvents';
import { materialityTrackerRouteEntry } from '@apps/materiality-tracker/routes/routes';
import { useAppSelector } from '@reducers/index';
import { getCurrentUser, isStaff } from '@selectors/user';
import { NonStaffOnboardingMTHoldingPage } from '@features/onboarding/NonStaffOnboardingMTHoldingPage';
import { isMaterialityTracker } from '@utils/apps';
import { ROUTES } from '@constants/routes';

type Props = Pick<BaseStepCompProps, 'appConfig'>;

export const CompletedMAT = ({ appConfig }: Props) => {
  const currentUser = useAppSelector(getCurrentUser);
  const isUserStaff = useAppSelector(isStaff);
  const hasAccessMaterialityTracker = currentUser?.initiativeTree?.some((i) => isMaterialityTracker(i));

  const history = useHistory();

  const redirect = () => {
    const returnPath = hasAccessMaterialityTracker
      ? generateUrl(materialityTrackerRouteEntry, {
          rootAppPath: appConfig.rootAppPath ?? 'materiality-tracker',
        })
      : generateUrl(ROUTES.COMPANY_TRACKER_LIST, {
          rootAppPath: 'company-tracker',
        });

    history.push(returnPath);
  };

  useEffect(() => {
    getAnalytics().track(AnalyticsEvents.OnboardingCompleted);
  }, []);

  // if the user is staff, allow them to access Materiality Tracker / Company Tracker
  // if user (staff/non-staff) requested to join CT while onboarding MT, allow them to access CT
  if (isUserStaff || !hasAccessMaterialityTracker) {
    return (
      <>
        <section className='title-container'>
          <div className='h1'>Done!</div>
          <div className='h4 text-strong mb-3'>Your account is now ready!</div>
        </section>
        <div className='mt-5 text-right mr-3'>
          <Button color='primary' onClick={redirect}>
            Continue to {hasAccessMaterialityTracker ? 'Materiality Tracker' : 'Company Tracker'}
          </Button>
        </div>
      </>
    );
  }
  // access for non-staff members is granted by staff admin via the staff portal
  return currentUser ? <NonStaffOnboardingMTHoldingPage user={currentUser} /> : null;
};
